<template>
  <div>
    <div class="verify-bank">
      <StepContent
        class="step-content"
        :theme="theme"
        :content-margin="'sm:mb-15'"
        :center-margin="'mb-5'"
      >
        <FlinksIframe
          v-if="!isVerified"
          :docs-stand-alone="docsStandAlone"
          :theme="theme"
          :event-prefix="eventPrefix"
          :instituition-id="instituitionId"
          @verified="handleVerified"
          @bank-select="handleBankSelect"
          @verify-bank="handleVerifyBank"
          @loaded="iframeLoaded = true"
        />
        <AppSpinner
          v-else
          class="m-auto"
          :color="hexColor()"
        />
      </StepContent>

      <template v-if="$_is_foundation">
        <div class="container">
          <p class="verify-bank__skip-text mb-10">
            Banking information is required in order to complete your Foundation Application.
          </p>
          <p class="verify-bank__skip-text">
            If you do not see your banking institution available, please
            <button
              class="font-bold underline"
              :class="textColorClass()"
              @click="triggerChat()"
            >
              click here
            </button> to chat with one of our agents.
          </p>
        </div>
      </template>

      <MockFlinks v-if="showMockFlinks && !isVerified" />

      <div
        v-if="!$_is_foundation && iframeLoaded"
        class="max-w-[346px] mx-auto"
      >
        <div class="verify-bank__no-bank mb-20">
          <p class="mt-10 verify-bank__no-bank-text">
            We do not store your credentials; banking connection is solely used for income verification during the approval process.
          </p>
        </div>
        <hr>
        <div>
          <div
            class="flex items-center justify-between py-20 cursor-pointer xxs-only:mx-[20px]"
            @click="triggerChat()"
          >
            <div class="flex gap-4">
              <div class="flex items-center">
                <img
                  class="mx-auto h-[33px]"
                  :src="require('@/assets/svg/chat-logo.svg')"
                  alt="chat logo"
                >
              </div>
              <div class="flex flex-col text-md">
                <span>Can’t login or connect to your bank? </span>

                <span><b>Chat with an agent.</b></span>
              </div>
            </div>
            <div>
              <FontAwesomeIcon
                icon="chevron-right"
                class="text-base font-bold"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <FreshChat
      :hide-chat-button="true"
      :load-chat="loadChat"
      @close-chat="loadChat = false"
    />
  </div>
</template>

<script>
import FlinksIframe from '@/components/common/flinks/FlinksIframe'
import MockFlinks from '@/components/common/flinks/MockFlinks'
import StepContent from '@/components/common/step-layout/StepContent'
import DataLayer from '@/utilities/data-layer'
import navigation from '@/mixins/navigation'
import submitLoans from '@/mixins/submit/submit-loans'
import AppSpinner from '@/components/common/spinners/AppSpinner'
import theme from '@/mixins/theme'
import tracking from '@/mixins/tracking'
import FreshChat from '@/components/widgets/freshchat/FreshChat'
import chatHandler from '@/mixins/freshchat/chat-handler'

export default {
  // Name
  name: 'VerifyBank',

  // Components
  components: {
    StepContent,
    FlinksIframe,
    MockFlinks,
    AppSpinner,
    FreshChat
  },

  // Mixins
  mixins: [
    navigation,
    submitLoans,
    theme,
    tracking,
    chatHandler
  ],

  // Props
  props: {
    docsStandAlone: {
      type: Boolean,
      default: false
    },

    title: {
      type: String,
      default: 'Which Bank Do You Currently Use?'
    },

    subtitle: {
      type: String,
      default: ''
    },

    showSkipText: {
      type: Boolean,
      default: true
    },

    showFooter: {
      type: Boolean,
      default: false
    },

    eventPrefix: {
      type: String,
      default: 'ploan'
    }
  },

  // Data
  data () {
    return {
      datalayerSkip: `spring_${this.eventPrefix}_verifybank_skip`,
      bankSelect: true,
      isVerified: false,
      forceShowMock: false,
      iframeLoaded: false
    }
  },

  // Computed
  computed: {
    cardImage () {
      let cardImage
      switch (this.theme) {
        case 'green': {
          cardImage = require(`@/assets/img/card_green.png`)
          break
        }
        case 'blue': {
          cardImage = require(`@/assets/img/card_green.png`)
          break
        }
        default: cardImage = require(`@/assets/img/card_blue.png`)
      }

      return cardImage
    },

    next () {
      return 'verify-identity'
    },

    isPersonalLoan () {
      return this.$store.getters['loans/isPersonalLoan']
    },

    returningCustomer () {
      return this.$store.state.returningCustomer.is_in_progress
    },

    datalayerComplete () {
      const eventName = this.returningCustomer ? `spring_${this.eventPrefix}_returning_customer_flinks_step_completed` : `spring_${this.eventPrefix}_bankdetails_step_completed`
      return { event: eventName }
    },

    showBankSkipStep () {
      return this.$route.meta.showBankSkipStep
    },

    bankSkipBasic () {
      return this.$route.meta.bankSkipBasic
    },

    bankName () {
      return this.$store.state.customer.bank
    },

    showMockFlinks () {
      return process.env.VUE_APP_MOCK_FLINKS_ENABLED || this.forceShowMock
    },

    isFoundationEligibleOnly () {
      return this.$store.getters.isFoundationEligibleOnly
    },

    instituitionId () {
      return this.$store.state.application.flinksInstitutionId
    }
  },

  watch: {
    $_navigation_triggerSkip: function (val) {
      if (val) this.handleSkip()
      this.$store.dispatch('setTriggerSkipStep', false)
    }
  },

  // Created
  created () {
    const mockFlinksEvent = process.env.VUE_APP_FORCE_SHOW_MOCK_FLINKS_EVENT
    if (mockFlinksEvent) window.addEventListener(mockFlinksEvent, this.handleForceShowMock)
    const eventName = this.returningCustomer
      ? `spring_${this.eventPrefix}_returning_customer_flinks_page_landed`
      : `spring_${this.eventPrefix}_flinks_page_landed`
    if (this.eventPrefix === 'ploan') {
      window.rudderanalytics.track(eventName, {
        bank: this.bankName
      })
    }
    DataLayer.pushObject({ event: eventName })
    this.$emit('show-bank-skip', true)
  },

  // Methods
  methods: {
    handleSkip () {
      DataLayer.pushEvent('event', this.datalayerSkip)
      this.$store.dispatch('setSkippedStep', true)
      this.$emit('show-bank-skip', false)
      this.$_navigation_handleNext()
    },

    async handleVerified () {
      this.$store.dispatch('setBankStatements', true)
      this.isVerified = true
      if (this.isPersonalLoan && this.$_navigation_shouldRedirectToPLDeclined) return this.$_navigation_handlePLDeclined()
      this.$_loans_submitLeadIfReady(false, false, this.datalayerComplete)
    },

    handleBankSelect () {
      this.bankSelect = true
      this.$emit('show-bank-skip', true)
    },

    handleVerifyBank () {
      this.bankSelect = false
      this.$emit('show-bank-skip', false)
    },

    handleForceShowMock () {
      this.forceShowMock = true
    }
  }
}
</script>

<style lang="scss">
.verify-bank {
  &__title {
    @apply text-3xl max-w-450 m-auto mb-20;
  }

  &__text {
    @apply text-base font-body font-normal max-w-650 mx-auto;

    @screen lg {
      @apply max-w-750;
    }
  }

  &__no-bank {
    @apply text-center;

    &-text {
      @apply text-center text-xs;

      button {
        @apply cursor-pointer font-semibold underline;
      }
    }
  }

  .step-footer {
    @apply max-w-465 md:max-w-full;
  }

  &__skip-text {
    @apply text-center text-xs;

    button {
      @apply cursor-pointer font-semibold underline;
    }
  }

  &-footer {
    @apply my-20 mx-auto;

    &__details {
      @apply flex flex-wrap py-0 px-20 mx-auto justify-center;

      @screen md {
        @apply flex py-30 px-40 my-60 flex-nowrap border-t-1 border-gray-300;

        img {
          @apply w-500 mx-auto md:mx-0;
        }
      }

      @screen lg {
        @apply grid items-start max-w-[1200px] mx-auto;

        grid-template-columns: repeat(2, 1fr);
        grid-column-gap: 180px;
      }

      strong {
        @apply cursor-pointer;
        @apply font-bold;
      }
    }

    &__text {
      @apply flex flex-col font-body text-base w-500 mx-auto md:mx-0;
    }

    &__figure {
      @apply mb-40;

      @screen md {
        @apply ml-0;

        flex-basis: 45%;
      }

      img {
        @apply min-w-145 max-w-full h-auto ml-15 md:mx-0;
      }
    }

    h3 {
      @apply font-bold font-body text-base mb-30;
    }

    section {
      p {
        @apply font-light font-body text-base mb-15;
      }
    }
  }
}
</style>
